"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Calendar, User, Loader, CircleAlert } from "lucide-react";



declare const wvcClient: any;

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="221794" className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-80 rounded-lg bg-muted/20 animate-pulse border border-border" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="bg-card border-border overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow duration-300 rounded-lg">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-muted/30">
              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" 
              />
            </div>
          )}
          <CardHeader className="p-6 pb-4">
            <CardTitle className="font-default text-xl font-bold leading-tight line-clamp-2 text-foreground">
              {post.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0 flex-grow">
            <div 
              className="text-muted-foreground text-sm line-clamp-3 font-sans" 
              dangerouslySetInnerHTML={{ __html: post.excerpt || '' }} 
            />
          </CardContent>
          <CardFooter className="p-6 pt-0 mt-auto">
            <Link to={post.link || "#"} className="inline-flex items-center text-secondary hover:text-secondary/80 text-sm font-semibold transition-colors">
              Read Insight <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPostId }: { currentPostId: string }) {
  
  const queryParams = { 
    post_type: 'post', 
    per_page: 3, 
    post__not_in: [currentPostId] 
  };
  const wp_query = new WP_Query(queryParams);

  return (
    <div className="border-t border-border pt-16 mt-24">
      <h2 className="text-3xl font-default font-bold mb-10 text-foreground">Further Clinical Reading</h2>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsGrid />
      </PostsProvider>
    </div>
  );
}

function PostDetailConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 flex flex-col items-center justify-center min-h-[50vh]">
        <Loader className="h-12 w-12 text-primary animate-spin mb-4" />
        <p className="text-muted-foreground font-sans">Syncing clinical data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 flex flex-col items-center justify-center min-h-[50vh] text-center">
        <CircleAlert className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-2xl font-default font-bold text-foreground mb-2">Unable to retrieve clinical data</h2>
        <p className="text-muted-foreground font-sans mb-6">{error}</p>
        <Link to="#">
          <Button variant="outline" className="rounded-md">Return to Home</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-12 flex flex-col items-center justify-center min-h-[50vh] text-center">
        <h2 className="text-2xl font-default font-bold text-foreground mb-2">Publication Not Found</h2>
        <p className="text-muted-foreground font-sans mb-6">The clinical resource you are looking for is currently unavailable or has been archived.</p>
        <Link to="#">
          <Button variant="outline" className="rounded-md">Return to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <div className="max-w-4xl mx-auto">
      {}
      <div className="mb-10">
        <Link to="#" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Eirion Insights
        </Link>
      </div>

      {}
      <header className="mb-12">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {post.categories.map((cat, i) => (
              <span key={cat.id} data-index={i} className="inline-flex items-center rounded-sm border border-border bg-accent/50 px-3 py-1 text-xs font-semibold text-accent-foreground">
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold mb-8 text-foreground leading-[1.1] tracking-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground font-sans border-b border-border pb-8">
          {post.author && (
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border border-border">
                <AvatarImage src={post.author.avatar?.['48'] || ''} alt={post.author.name} />
                <AvatarFallback className="bg-muted text-muted-foreground">
                  <User className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 opacity-70" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-16 rounded-lg overflow-hidden border border-border shadow-sm bg-muted/10">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover aspect-[21/9] md:aspect-[2/1]" 
          />
        </div>
      )}

      {}
      <article 
        className="font-sans text-lg leading-relaxed text-muted-foreground 
                   [&>h2]:text-3xl [&>h2]:font-default [&>h2]:font-bold [&>h2]:text-foreground [&>h2]:mt-16 [&>h2]:mb-6 
                   [&>h3]:text-2xl [&>h3]:font-default [&>h3]:font-bold [&>h3]:text-foreground [&>h3]:mt-12 [&>h3]:mb-4 
                   [&>h4]:text-xl [&>h4]:font-default [&>h4]:font-semibold [&>h4]:text-foreground [&>h4]:mt-8 [&>h4]:mb-4 
                   [&>p]:mb-6 
                   [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2
                   [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2
                   [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:my-8 [&>blockquote]:text-foreground/90
                   [&_a]:text-secondary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-secondary/80
                   [&_img]:rounded-lg [&_img]:border [&_img]:border-border [&_img]:my-8 [&_img]:w-full [&_img]:h-auto
                   [&_pre]:bg-muted/50 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:border [&_pre]:border-border [&_pre]:my-6
                   [&_code]:bg-muted/50 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded-md [&_code]:text-sm [&_code]:font-mono"
        dangerouslySetInnerHTML={{ __html: post.content || '' }} 
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border flex flex-wrap gap-2 items-center">
          <span className="text-sm font-medium text-muted-foreground mr-2">Keywords:</span>
          {post.tags.map((tag, i) => (
            <span key={tag.id} data-index={i} className="inline-flex items-center rounded-sm bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {}
      <RelatedPosts currentPostId={post.id} />
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'post' }; 
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-8 w-full">
      <div className="max-w-7xl mx-auto">
        <PostsProvider wp_query={wp_query}>
          <PostDetailConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}